import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { transcriptsAPI } from '../../services/api';
import { formatFileSize } from '../../hooks/useFileUpload';
import './Upload.css';

function UploadProgress({ files, allDone, onComplete, onReset, onFileStatusChange, isMobile }) {
  const navigate = useNavigate();
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const [connectionWarning, setConnectionWarning] = useState(false);

  const processingFiles = files.filter(f => f.status === 'processing');
  const completedFiles = files.filter(f => f.status === 'completed');
  const failedFiles = files.filter(f => f.status === 'failed');
  const uploadingFiles = files.filter(f => f.status === 'uploading');
  const pendingFiles = files.filter(f => f.status === 'pending');

  // Poll all processing files
  useEffect(() => {
    if (processingFiles.length === 0) return;

    let cancelled = false;

    const pollAll = async () => {
      if (cancelled) return;

      let hadError = false;

      for (const file of processingFiles) {
        if (cancelled) return;
        try {
          const response = await transcriptsAPI.getStatus(file.transcriptId);
          const status = response.data.status;

          if (status === 'completed') {
            onFileStatusChange(file.id, 'completed', null);
          } else if (status === 'failed') {
            onFileStatusChange(file.id, 'failed', response.data.error_message || 'Transcription failed');
          }
        } catch {
          hadError = true;
        }
      }

      if (hadError) {
        setConsecutiveErrors(c => c + 1);
        if (consecutiveErrors >= 5) {
          setConnectionWarning(true);
        }
      } else {
        setConsecutiveErrors(0);
        setConnectionWarning(false);
      }

      if (!cancelled) {
        setTimeout(pollAll, 3000);
      }
    };

    pollAll();

    return () => { cancelled = true; };
  }, [processingFiles.map(f => f.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSummaryText = () => {
    if (uploadingFiles.length > 0) {
      const doneCount = completedFiles.length + failedFiles.length;
      return `Uploading file ${doneCount + 1} of ${files.length}...`;
    }
    if (processingFiles.length > 0) {
      return `Processing ${processingFiles.length} file${processingFiles.length !== 1 ? 's' : ''}...`;
    }
    if (allDone) {
      if (failedFiles.length === 0) {
        return `All ${completedFiles.length} file${completedFiles.length !== 1 ? 's' : ''} transcribed!`;
      }
      if (completedFiles.length === 0) {
        return `All ${failedFiles.length} file${failedFiles.length !== 1 ? 's' : ''} failed.`;
      }
      return `${completedFiles.length} completed, ${failedFiles.length} failed.`;
    }
    if (pendingFiles.length > 0) {
      return `Preparing ${pendingFiles.length} file${pendingFiles.length !== 1 ? 's' : ''}...`;
    }
    return 'Processing...';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'uploading': return 'status-uploading';
      case 'processing': return 'status-processing';
      case 'completed': return 'status-completed';
      case 'failed': return 'status-failed';
      default: return '';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'uploading': return 'Uploading';
      case 'processing': return 'Processing';
      case 'completed': return 'Complete';
      case 'failed': return 'Failed';
      default: return status;
    }
  };

  const handleViewFiles = () => {
    onComplete();
    navigate(isMobile ? '/mobile/files' : '/files');
  };

  return (
    <div className="upload-container">
      <div className="progress-card card">
        <div className="batch-summary">
          <span className="batch-summary-text">{getSummaryText()}</span>
        </div>

        {connectionWarning && (
          <div className="alert alert-warning">
            Having trouble connecting. Transcriptions may still be processing...
          </div>
        )}

        <div className="batch-file-list">
          {files.map((file) => (
            <div key={file.id} className="batch-file-row">
              <div className="batch-file-info">
                <span className="batch-file-icon">
                  {file.file.type?.startsWith('video/') ? '🎬' : '🎵'}
                </span>
                <div className="batch-file-details">
                  <span className="batch-file-name">{file.file.name}</span>
                  <span className="batch-file-size">{formatFileSize(file.file.size)}</span>
                </div>
                <span className={`status-badge ${getStatusBadgeClass(file.status)}`}>
                  {getStatusLabel(file.status)}
                </span>
              </div>

              {file.status === 'uploading' && (
                <div className="batch-inline-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${file.uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {file.status === 'failed' && file.error && (
                <div className="batch-file-error">{file.error}</div>
              )}
            </div>
          ))}
        </div>

        {(processingFiles.length > 0 || uploadingFiles.length > 0) && (
          <p className="progress-hint">
            This may take a few minutes depending on file length.
          </p>
        )}

        <div className="progress-actions">
          {allDone && completedFiles.length > 0 && (
            <button
              className="btn btn-primary btn-lg"
              onClick={handleViewFiles}
            >
              View Files
            </button>
          )}

          {allDone && (
            <button
              className="btn btn-secondary"
              onClick={onReset}
            >
              Upload More
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default UploadProgress;
